import React, {useEffect, useState} from 'react';
import {useQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import {
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress, Container,
    ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary,
    Grid,
    Paper, TextField,
    Typography
} from "@material-ui/core";
import useStyles from "../styles/styles";
import {Alert, Autocomplete} from "@material-ui/lab";
import {navigate} from "@reach/router";
import AddIcon from "@material-ui/icons/Add";
import getEnvironment from "../utils/getEnvironment";
import PixelsTable from "../components/Pixels/PixelsTable";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import _ from "lodash";
import useFormInputs from "../hooks/useFormInputs";

const Pixels = () => {
    const classes = useStyles();
    const env = getEnvironment();
    const [variables, setVariables] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const onSubmit = () => {
        const finalInputs = _.omitBy(inputs, _.isEmpty);
        setIsLoading(true);
        setVariables(finalInputs);
    };

    const {clearInput, clearAllInputs, inputs, setInputs, handleInputChange, handleSubmit} = useFormInputs(onSubmit);
    const {loading, error, data, refetch} = useQuery(gql`
        query PixelsPage {
            allPixels {
                pixelName
                pixelKey
                httpMethod
                id
                pixelQueryParameters {
                    id
                    pixelId
                    parameterName
                    sourceData
                    constantValue
                }
                pixelSubscribers {
                    id
                    pixelId
                    affiliateId
                    trafficSourceId
                    revenueType
                }
                url
            }
            allAffiliate {
                nodes {
                    id
                    name
                }
            }
            allTrafficSource {
                nodes {
                    id
                    name
                }
            }
        }
    `, {
        notifyOnNetworkStatusChange: true,
        context: {
            clientName: "kgraphql"
        }
    });

    useEffect(() => {
        setIsLoading(false)
    }, [data])

    useEffect(() => {
        if (isLoading) {
            setTimeout(() => setIsLoading(false), 3000);
        }
    }, [isLoading])

    const handleChange = (name, value) => {
        setInputs(inputs => ({
            ...inputs,
            [name]: value ? value : ''
        }));
    };

    const findOption = (options, id) => {
        const option = options.find(item => item.id === id)
        return option ? `${option.name} (${id})` : null
    }

    if (loading || isLoading || !data || !data.allPixels || !data.allAffiliate || !data.allTrafficSource) {
        return (
            <Backdrop className={classes.backdrop} open={true}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        );
    }

    return (
        <>
            <Typography variant="h2">
                Pixels
            </Typography>
            <form id="pixel-form" onSubmit={handleSubmit}>
                <ExpansionPanel expanded style={{marginBottom: '20px'}}>
                    <ExpansionPanelSummary
                        expandIcon={<ExpandMoreIcon/>}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography variant="h5">Filter Pixels</Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails className={classes.filter}>
                        <Container maxWidth={false}>
                            <Box className={classes.filterBox} style={{marginTop: 0}}>
                                <Chip
                                    label="Default Options"
                                    className={classes.filterSectionLabels}
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={9}>
                                        <TextField
                                            id="search"
                                            name="pixelId"
                                            label="Search by UUID"
                                            value={inputs.pixelId || ''}
                                            onChange={handleInputChange}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Button variant="contained" color="secondary" fullWidth onClick={() => {
                                            window.open(`/info/pixels/${inputs.pixelId}`);
                                            clearInput('pixelId');
                                        }}>
                                            Find Pixel
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box className={classes.filterBox}>
                                <Chip
                                    label="Filter Options"
                                    className={classes.filterSectionLabels}
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={3}>
                                        {data && data.allAffiliate.nodes.length ? (
                                            <Autocomplete
                                                className={classes.autoComplete}
                                                name="affiliateId"
                                                value={inputs.affiliateId || null}
                                                onChange={(e, val) => handleChange('affiliateId', val)}
                                                options={data.allAffiliate.nodes.map(entry => entry.id)}
                                                getOptionLabel={option => findOption(data.allAffiliate.nodes, option)}
                                                renderInput={params => <TextField {...params} label="Affiliates"
                                                                                  variant="outlined" fullWidth/>}
                                            />
                                        ) : null}
                                    </Grid>
                                    <Grid item xs={3}>
                                        {data && data.allTrafficSource.nodes.length ? (
                                            <Autocomplete
                                                className={classes.autoComplete}
                                                name="trafficSourceId"
                                                value={inputs.trafficSourceId || null}
                                                onChange={(e, val) => handleChange('trafficSourceId', val)}
                                                options={data.allTrafficSource.nodes.map(entry => entry.id)}
                                                getOptionLabel={option => findOption(data.allTrafficSource.nodes, option)}
                                                renderInput={params => <TextField {...params} label="Traffic Sources"
                                                                                  variant="outlined" fullWidth/>}
                                            />
                                        ) : null}
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box paddingTop={3}>
                                <Grid container spacing={2} justify="center" alignItems="center">
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <Button type="submit" variant="contained" color="secondary" fullWidth>
                                            Filter Results
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <Button onClick={() => {
                                            clearAllInputs();
                                            setVariables({});
                                        }} variant="outlined" color="secondary" fullWidth>
                                            Clear Filters
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Container>
                    </ExpansionPanelDetails>
                </ExpansionPanel>
            </form>
            <Grid container direction="row" justify="space-between" alignItems="center" style={{marginBottom: '15px'}}>
                <Grid item xs={4}>
                </Grid>
                <Grid item xs={4} style={{textAlign: 'right'}}>
                    {env === 'test' ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate('/info/pixels/add')}
                            startIcon={<AddIcon/>}>
                            ADD PIXEL
                        </Button>
                    ) : (
                        <></>
                    )}
                </Grid>
            </Grid>
            {
                !loading && error ? (
                    <Paper className={classes.paper}>
                        <Alert severity="error">There is an error!</Alert>
                        <pre>{JSON.stringify(error, null, 2)}</pre>
                    </Paper>
                ) : null
            }
            {
                loading ? (
                    <Paper className={classes.paper}>
                        <div className={classes.loadingBar}>
                            <CircularProgress
                                size={50}
                                thickness={4}
                            />
                        </div>
                    </Paper>
                ) : null
            }
            {
                !loading && data && data.allPixels.length ? (
                    <>
                        <PixelsTable
                            graphqlData={data}
                            pixelData={data.allPixels}
                            variables={variables}
                            refetch={refetch}
                        />
                    </>
                ) : null
            }
        </>
    );
};

export default Pixels;
